import { queryOptions, useMutation, useQuery } from '@tanstack/react-query';
import { type StaticDecode, Type } from 'typebox';
import * as TypeCompiler from 'typebox/compile';
import { ReadonlyArray, ReadonlyObject } from 'schema/schema';
import { type LogEntry, useLogUpdatedCVs } from 'stores/ActivityLogs';
import { UsageUser } from 'stores/ProjectFieldOverview';
import { http, httpChecked, queryClient } from 'util/fetch';

const ProjectDescription = ReadonlyObject({
  project_description: Type.String(),
  count: Type.Integer(),
  users: ReadonlyArray(UsageUser),
});

const checker = TypeCompiler.Compile(ReadonlyArray(ProjectDescription));

type ProjectDescription = StaticDecode<typeof ProjectDescription>;
export type ProjectDescriptions = readonly ProjectDescription[];

const queries = {
  descriptions: ['project-descriptions'] as const,
  fetch: (customer_id: string, project_id: string, language_code: string) =>
    queryOptions({
      queryKey: [
        ...queries.descriptions,
        { customer_id, project_id },
        { language_code },
      ] as const,
      queryFn: ({ signal }) =>
        httpChecked.get(
          `/api/v2/company/cv/customers/unapproved/project_experiences/project_description?language_code=${language_code}&customer_id=${customer_id}&project_id=${project_id}`,
          checker,
          { signal },
        ),
    }),
};

export function useProjectDescriptions(
  customer_id: string,
  project_id: string,
  language_code: string,
  enabled = true,
): ProjectDescriptions | undefined {
  const { data } = useQuery({
    ...queries.fetch(customer_id, project_id, language_code),
    staleTime: 60_000,
    enabled,
  });

  return data;
}

export function usePollingProjectDescriptions(
  customer_id: string,
  project_id: string,
  language_code: string,
  enabled: boolean,
) {
  useQuery({
    ...queries.fetch(customer_id, project_id, language_code),
    refetchInterval: 1_000,
    enabled,
  });
}

interface ChangeProjectDescription {
  original_value: string;
  new_value: string;
  customer_id: string;
  project_id: string;
  language_code: string;
  limit_to_user_ids: readonly string[];
}

export function useChangeProjectDescription() {
  const logUpdatedCVs = useLogUpdatedCVs();

  return useMutation({
    mutationFn: ({ language_code, ...body }: ChangeProjectDescription) =>
      http.put<{ updated_cvs: readonly LogEntry[] }>(
        `/api/v2/update-terms/${language_code}/project_experiences/long_description`,
        { body },
      ),
    onSuccess: ({ updated_cvs }) => logUpdatedCVs(updated_cvs),
    onSettled: (
      _data,
      _error,
      { customer_id, project_id, language_code },
      _onMutateResult,
      { client },
    ) =>
      client.invalidateQueries(
        queries.fetch(customer_id, project_id, language_code),
      ),
  });
}

export async function prefetchProjectDescriptions(
  customer_id: string,
  project_id: string,
  language_code: string,
) {
  await queryClient.prefetchQuery({
    ...queries.fetch(customer_id, project_id, language_code),
    staleTime: 60_000,
  });
}

export async function invalidateProjectDescriptionsForProject(
  customer_id: string,
  project_id: string,
) {
  // language will be popped off
  const { queryKey } = queries.fetch(customer_id, project_id, 'fake');
  const allCustomerKey = queryKey.slice(0, -1);

  await queryClient.invalidateQueries({ queryKey: allCustomerKey });
}

export async function invalidateAllProjectDescriptions() {
  await queryClient.invalidateQueries({ queryKey: queries.descriptions });
}
